import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	FormLabel,
	Stack,
	Typography,
	TextField,
	FormHelperText,
	useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { FolderSelectContextProvider } from '@/context/folderSelectContext';
import { useFormikContext } from 'formik';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import getCorrectFolderOwner from '@/hooks/getCorrectFolderOwner';
import {
	useAddFolderToSharepointMutation,
	useGetDriveItemQuery,
} from '@/features/msGraph/msGraphApi';
import { getDriveItemFolderDisplayPath } from '@/features/msGraph/helper';
import { FOLDER_NAME_MAX } from '../AddFolderSharepointDialog/validationSchema';
import { replaceFolderName } from '@/utils/constants';

export const FolderSelectDialog = ({ initialItemId, open, handleClose, onConfirm }) => {
	const formik = useFormikContext();
	const { values, setFieldValue } = formik;
	const theme = useTheme();

	const [folderName, setFolderName] = useState('');
	const [error, setError] = useState(false);

	useEffect(() => {
		if (!open) setFolderName('');
	}, [open]);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const [createSharepointFolder, creatingState] = useAddFolderToSharepointMutation();

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { correctOwner: folderOwnerId } = getCorrectFolderOwner({
		deal: values.deal,
		client: values.client,
		relationshipCompany: values.relationshipCompany,
	});

	const { data: folderOwner } = useGetUserDetailsQuery(
		{ userId: folderOwnerId },
		{ skip: !folderOwnerId },
	);

	const itemId =
		initialItemId ??
		values.relationshipCompany?.driveId ??
		values.deal?.driveId ??
		values.client?.driveId ??
		'root';
	const driveId =
		values.relationshipCompany?.clientFolderDriveId ??
		values.deal?.clientFolderDriveId ??
		values.client?.clientFolderDriveId ??
		folderOwner?.clientFolderDriveId ??
		userDetails?.clientFolderDriveId;

	useEffect(() => {
		setFieldValue('driveId', driveId);
	}, [driveId]);

	const { currentData: driveItem, ...driveItemQuery } = useGetDriveItemQuery(
		{
			driveId,
			itemId: values.selectedFolder,
		},
		{ skip: !values.selectedFolder || !driveId },
	);

	const hasSelection = values.selectedFolder && driveItem?.webUrl;

	useEffect(() => {
		if (values.selectedDrive != driveItem) {
			console.log('Drive item in Folder Select', driveItem);
			setFieldValue('selectedDrive', driveItem);
		}
	}, [setFieldValue, driveItem, values.selectedDrive]);

	const webUrl = useMemo(() => {
		if (!hasSelection) return 'None';
		return getDriveItemFolderDisplayPath(driveItem, folderOwner, userDetails);
	}, [hasSelection, driveItem, folderOwner, userDetails]);

	useEffect(() => {
		if (values.folderPath != webUrl) {
			setFieldValue('folderPath', webUrl === 'None' ? '' : webUrl);
		}
	}, [setFieldValue, webUrl, values.folderPath]);

	const notifySelection = () => {
		if (!values.selectedFolder) {
			return;
		}

		onConfirm?.({
			selectedFolder: values.selectedFolder,
			selectedDrive: values.selectedDrive,
			folderPath: values.folderPath,
		});
	};

	const handleCreateFolder = async (name) => {
		const res = await createSharepointFolder({
			driveId: values.selectedDrive?.parentReference?.driveId,
			itemId: values?.selectedDrive?.id,
			folderName: name.trim(),
		}).unwrap();
		setFieldValue('selectedFolder', res.id);
		return res;
	};

	const handleConfirm = async () => {
		if (!folderName) {
			notifySelection();
			return handleClose();
		}
		const createdFolder = await handleCreateFolder(folderName.trim());
		onConfirm?.({
			selectedFolder: createdFolder.id,
			selectedDrive: createdFolder,
			folderPath: values.folderPath,
		});
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason === 'backdropClick' && creatingState.isLoading) return;
				handleClose();
			}}
			hideBackdrop={true}
			sx={{
				padding: '1em',
				'& .MuiDialog-paper': {
					width: '60%',
				},
			}}
		>
			<DialogTitle>Select folder</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<Stack spacing={2}>
					{/* <Stack spacing={1}>
						<FormLabel>Selected folder</FormLabel>
						<Typography
							variant={hasSelection ? 'blue_text' : 'avatar_subtitle'}
							sx={{
								...(hasSelection && { fontWeight: 'bold' }),
							}}
						>
							{webUrl}
						</Typography>
					</Stack> */}

					<FolderSelectContextProvider
						{...{
							selectedFolder: values.selectedFolder,
							setSelectedFolder: (folder) => setFieldValue('selectedFolder', folder),
							itemId,
							driveId,
							disableParent: itemId === 'root',
							cannotSelect: (folder) => folder.id === 'root',
							allowCreate: true,
						}}
					/>

					<Stack spacing={1}>
						<FormLabel>
							{'Create new folder'}
							<span
								style={{
									fontWeight: 'normal',
									fontSize: '12px',
									marginLeft: '1em',
								}}
							>
								{'(Optional)'}
							</span>
						</FormLabel>
						<Stack>
							<TextField
								value={folderName}
								disabled={!values.selectedDrive?.id || creatingState.isLoading}
								onChange={(e) => setFolderName(e?.target?.value ?? '')}
								slotProps={{
									input: {
										max: FOLDER_NAME_MAX,
									},
								}}
								onBlur={async (e) => {
									const v = replaceFolderName(e?.target?.value ?? '').trim();
									if (v && v.length <= FOLDER_NAME_MAX) {
										setFolderName(v);
									}
									setError(v && v.length > FOLDER_NAME_MAX);
								}}
								error={error}
							/>
							<Stack
								direction='row'
								justifyContent={error ? 'space-between' : 'flex-end'}
								width='100%'
							>
								{error && (
									<FormHelperText
										error
									>{`Name cannot be longer than ${FOLDER_NAME_MAX} characters`}</FormHelperText>
								)}

								<FormHelperText
									error={error || folderName.length > FOLDER_NAME_MAX}
									sx={{
										...(folderName.length >= FOLDER_NAME_MAX && {
											fontWeight: 'bold',
										}),
										mr: '14px',
										fontSize: '0.7rem',
										color: theme.palette.text.secondary,
									}}
								>
									<span>{folderName.length}</span>
									{`/${FOLDER_NAME_MAX}`}
								</FormHelperText>
							</Stack>
						</Stack>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					variant='text'
					disabled={creatingState.isLoading}
					color='primary'
					onClick={handleClose}
				>
					Close
				</Button>
				<Button
					variant='contained'
					disabled={
						creatingState.isLoading ||
						!values.selectedDrive?.id ||
						(folderName.length > 0 && error)
					}
					color='primary'
					onClick={handleConfirm}
				>
					{folderName.length === 0 ? 'Select folder' : 'Create new folder'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
